import { isValidKvnr } from "./kvnr.js";
import { parseBirthDate } from "./patient.js";

/**
 * Auswertung von OCR-Text einer Gesundheitskarte (A06, lokales OCR im Browser).
 * Unterstützt die Vorderseite (Name, KVNR, IK, Kasse) und die Rückseite (EHIC: Felder 3–7).
 * Liefert nur, was sicher erkannt wurde – alles andere bleibt leer und wird von Hand ergänzt.
 */

export interface EgkOcrFields {
  vorname: string;
  name: string;
  kvnr: string;
  ik: string;
  krankenkasse: string;
  geburtsdatum: string;
}

const EMPTY: EgkOcrFields = { vorname: "", name: "", kvnr: "", ik: "", krankenkasse: "", geburtsdatum: "" };

/** Typische OCR-Verwechslungen in Ziffernfolgen */
const toDigits = (s: string) =>
  s
    .replace(/[OoQD]/g, "0")
    .replace(/[Il|!]/g, "1")
    .replace(/[Ss]/g, "5")
    .replace(/B/g, "8")
    .replace(/Z/g, "2");

const KASSE =
  /\b(AOK|BKK|IKK|DAK|KKH|HEK|SBK|hkk|TK|Techniker|Barmer|Knappschaft|mhplus|Krankenkasse|Ersatzkasse|Gesundheitskasse|Betriebskrankenkasse|Innungskrankenkasse|Viactiv|Novitas|Mobil)\b/i;
const NOISE =
  /(gesundheitskarte|european|health|insurance|card|versichert|karte|kennnummer|ablauf|geburt|name|vorname|träger|traeger|persönlich|personlich|\d)/i;

const titleCase = (s: string) =>
  s.toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_, sep: string, ch: string) => sep + ch.toUpperCase());

const isNameLine = (l: string) =>
  /^[\p{L}][\p{L}'.-]*(\s+[\p{L}][\p{L}'.-]*){1,4}$/u.test(l) && !NOISE.test(l) && !KASSE.test(l);

function findKvnr(lines: string[]): { kvnr: string; line: number } | null {
  let fallback: { kvnr: string; line: number } | null = null;
  for (let i = 0; i < lines.length; i++) {
    for (const m of lines[i]!.matchAll(
      /\b([A-Z])\s?([0-9OoIlSBZ|]{3}\s?[0-9OoIlSBZ|]{3}\s?[0-9OoIlSBZ|]{3})\b/g,
    )) {
      const k = m[1]! + toDigits(m[2]!.replace(/\s/g, ""));
      if (!/^[A-Z]\d{9}$/.test(k)) continue;
      if (isValidKvnr(k)) return { kvnr: k, line: i };
      fallback ??= { kvnr: k, line: i };
    }
  }
  return fallback;
}

function findIk(text: string): { ik: string; index: number } | null {
  const m = /\b(10[0-9OoIlSBZ]{7})\b/.exec(text);
  if (!m) return null;
  const ik = toDigits(m[1]!);
  return /^\d{9}$/.test(ik) ? { ik, index: m.index + m[0].length } : null;
}

function findBirthDate(text: string, now: Date): string {
  for (const m of text.matchAll(/\b(\d{2})\s?[./-]\s?(\d{2})\s?[./-]\s?(\d{4})\b/g)) {
    const v = `${m[1]}.${m[2]}.${m[3]}`;
    if (parseBirthDate(v, now)) return v;
  }
  return "";
}

/** Wert hinter einem nummerierten EHIC-Feld („3. Name MUSTERMANN“ oder Wert in der Folgezeile) */
function ehicField(lines: string[], label: RegExp): string {
  for (let i = 0; i < lines.length; i++) {
    const m = label.exec(lines[i]!);
    if (!m) continue;
    const rest = lines[i]!.slice(m.index + m[0].length)
      .replace(/^[\s:.-]+/, "")
      .trim();
    const v = rest || lines[i + 1]?.trim() || "";
    if (/^[\p{L}][\p{L}' .-]*$/u.test(v)) return titleCase(v);
  }
  return "";
}

export function parseEgkText(
  text: string,
  now: Date = new Date(),
): { fields: EgkOcrFields; found: Array<keyof EgkOcrFields> } {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  const f: EgkOcrFields = { ...EMPTY };

  const kv = findKvnr(lines);
  if (kv) f.kvnr = kv.kvnr;
  const ik = findIk(text);
  if (ik) f.ik = ik.ik;
  f.geburtsdatum = findBirthDate(text, now);

  // Rückseite (EHIC): 3 = Name, 4 = Vornamen, 7 = Träger („108310400 - AOK Bayern“)
  f.name = ehicField(lines, /(?:^|\s)3\.?\s*(?:Name|Surname|Nom)\b/i);
  f.vorname = ehicField(lines, /(?:^|\s)4\.?\s*(?:Vornamen?|Given names?|Prénoms?)\b/i);

  if (ik) {
    const after = text
      .slice(ik.index, ik.index + 80)
      .split(/\r?\n/)[0]!
      .replace(/^[\s:-]+/, "")
      .trim();
    if (after && KASSE.test(after)) f.krankenkasse = after;
  }
  if (!f.krankenkasse) {
    const line = lines.find((l) => KASSE.test(l) && !/\d{5,}/.test(l));
    if (line) f.krankenkasse = line;
  }

  // Vorderseite: Namenszeile steht über der KVNR (Vorname(n) … Nachname)
  if (!f.name && kv) {
    for (let i = kv.line; i >= Math.max(0, kv.line - 3); i--) {
      const candidate =
        (i === kv.line ? lines[i]!.slice(0, lines[i]!.indexOf(kv.kvnr[0]!)).trim() : lines[i]!) ?? "";
      if (isNameLine(candidate)) {
        const parts = candidate.split(" ");
        f.name = titleCase(parts.pop()!);
        f.vorname = titleCase(parts.join(" "));
        break;
      }
    }
  }

  const found = (Object.keys(f) as Array<keyof EgkOcrFields>).filter((k) => f[k]);
  return { fields: f, found };
}
